import React, {
    ChangeEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import Icon from 'flow/components/ui-components/Icon';
import SearchInput from 'flow/components/ui-components/SearchInput';
import { segmentTrack } from 'flow/actions/segment-actions';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import useKeyboardShortcuts from 'flow/hooks/useKeyboardShortcuts';
import { normalizeShortcutToDisplay } from 'flow/utils/shortcut-utils';
import { getKeyboardShortcuts } from 'flow/selectors/keyboard-shortcuts-selectors';
import { selectHasTicketsAdvancedSearch } from 'flow/selectors/feature-flag-selectors';
import { AdvancedSearchFilters } from 'flow/types/Tickets';
import AdvancedSearchPanelContent from './AdvancedSearchPanelContent';
import { FILTER_POPUP_MARKER_ATTR } from './constants';

interface Props {
    onSearchQueryChange: (event: ChangeEvent<HTMLInputElement>) => void;
    /**
     * Called when the user commits filter changes from the popup —
     * either Apply (with the draft) or Reset (with empty filters).
     * The parent refreshes `ticketsSearchParams` and, for non-empty
     * filters, switches the tab to ALL.
     */
    onFiltersApply: (next: AdvancedSearchFilters) => void;
    appliedAdvancedSearchFilters: AdvancedSearchFilters;
    hasActiveAdvancedFilters: boolean;
}

const TicketsSearchInputSection = ({
    onSearchQueryChange,
    onFiltersApply,
    appliedAdvancedSearchFilters,
    hasActiveAdvancedFilters,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const hasTicketsAdvancedSearch = useAppSelector(
        selectHasTicketsAdvancedSearch,
    );
    const inputRef = useRef<HTMLInputElement>(null);
    const [input, setInput] = useState('');
    const { callSearchShortcut } = useKeyboardShortcuts();
    const shortcuts = useAppSelector(getKeyboardShortcuts);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (callSearchShortcut('general', event)) {
                event.preventDefault();
                inputRef.current?.focus();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [callSearchShortcut]);

    const handleSearchQueryChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const inputNew = event.target.value;
            if (!input && inputNew) {
                dispatch(
                    segmentTrack(TICKET_STRINGS.SEARCH_BEGAN_TYPING, {
                        component: 'Search bar',
                    }),
                );
            }
            setInput(inputNew);
            onSearchQueryChange(event);
        },
        [dispatch, input, onSearchQueryChange],
    );

    const shortcutText = (type: string) => {
        const matchedShortcut = shortcuts.find(
            (shortcut) =>
                shortcut.get('enabled') && shortcut.get('label') === type,
        );
        if (!matchedShortcut) {
            return '';
        }
        const shortcutText = normalizeShortcutToDisplay(
            matchedShortcut?.get('value'),
        );
        return `[${shortcutText}]`;
    };

    const searchInput = (
        <SearchInput
            data-test-id={'search-bar-tickets'}
            iconKey="search"
            placeholder={`${i18next.t('input.search')} ${shortcutText('extension_list_search')}`}
            onChange={handleSearchQueryChange}
            ref={inputRef}
            className={hasTicketsAdvancedSearch ? 'pe-10' : undefined}
        />
    );

    if (!hasTicketsAdvancedSearch) {
        return (
            <div className="flex flex-col gap-y-2 px-3">
                <div className="relative">{searchInput}</div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-y-2 px-3">
            <Popover className="relative z-10">
                <div className="relative">
                    {searchInput}
                    <PopoverButton
                        aria-label={i18next.t('tickets.filter.advanced')}
                        className={twMerge(
                            'absolute end-1 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-sm hover:bg-gray-300',
                            hasActiveAdvancedFilters &&
                                'bg-main text-iconOnBrand hover:darken-10',
                        )}
                    >
                        <Icon iconKey="setting_menu" size="1.25rem" />
                    </PopoverButton>
                </div>
                <PopoverPanel
                    {...{ [FILTER_POPUP_MARKER_ATTR]: '' }}
                    className="shadow-base absolute inset-x-0 top-full z-20 mt-1 rounded-sm bg-white p-3"
                >
                    {({ close }) => (
                        <AdvancedSearchPanelContent
                            filters={appliedAdvancedSearchFilters}
                            onApply={onFiltersApply}
                            onClose={close}
                        />
                    )}
                </PopoverPanel>
            </Popover>
        </div>
    );
};

export default TicketsSearchInputSection;
